import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { z } from "zod";
import { AppShell } from "@/components/layouts/AppShell";
import { PriceTag } from "@/components/PriceTag";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { api, ApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * /checkout/success — Stripe post-payment landing page.
 *
 * Flow after a successful Stripe Checkout Session:
 *   1. Stripe redirects here with ?session_id=cs_test_...
 *   2. We poll /api/v1/me/pass every 2s (up to ~20s total) waiting for the
 *      webhook to mint the ticket + entitlement. In practice the webhook
 *      fires within 100-500ms of session completion, so the first poll
 *      typically succeeds.
 *   3. Once has_active_pass=true, we show the pass card + link to the
 *      calendar (paywall is now unlocked).
 *
 * If the user is somehow unauthenticated when they land here (rare — they
 * were authed to reach checkout in the first place, but the tab could have
 * been closed and re-opened after Stripe fired the email receipt), we show
 * a "Sign in to see your pass" fallback with a signin CTA.
 */

const searchSchema = z.object({
  session_id: z.string().optional(),
});

export const Route = createFileRoute("/checkout/success")({
  validateSearch: searchSchema,
  component: CheckoutSuccessPage,
});

interface PassResp {
  passes: Array<{
    ticket_id: string;
    status: string;
    product: { id: string; name: string; access_level_code: string };
    purchase: { id: string; total_cents: number; currency: string; purchased_at: string; psp_ref: string | null };
    entitlement: { resource_type: string; valid_from: string | null; valid_to: string | null } | null;
    qr: { token: string; image_url: string };
  }>;
  has_active_pass: boolean;
}

function CheckoutSuccessPage() {
  const search = Route.useSearch();
  const { user } = useAuth();

  const [state, setState] = useState<
    | { kind: "polling"; attempts: number }
    | { kind: "ready"; data: PassResp }
    | { kind: "not_yet"; data: PassResp; attempts: number }
    | { kind: "error"; message: string }
    | { kind: "unauth" }
  >({ kind: "polling", attempts: 0 });

  useEffect(() => {
    if (!user) {
      setState({ kind: "unauth" });
      return;
    }

    let cancelled = false;
    let attempts = 0;
    const MAX_ATTEMPTS = 10; // 10 × 2s = 20s

    async function poll() {
      while (!cancelled && attempts < MAX_ATTEMPTS) {
        attempts++;
        try {
          const data = await api<PassResp>("/api/v1/me/pass");
          if (cancelled) return;
          if (data.has_active_pass) {
            setState({ kind: "ready", data });
            return;
          }
          setState({ kind: "not_yet", data, attempts });
        } catch (e) {
          if (cancelled) return;
          const msg = e instanceof ApiError ? (e.problem.detail ?? e.message) : String((e as Error).message);
          setState({ kind: "error", message: msg });
          return;
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) {
        setState((prev) =>
          prev.kind === "not_yet"
            ? { kind: "error", message: "Your payment was received but your pass hasn't landed yet. Refresh in a minute or contact support." }
            : prev,
        );
      }
    }

    poll();
    return () => { cancelled = true; };
  }, [user]);

  return (
    <AppShell>
      <div className="container max-w-2xl py-10">
        {state.kind === "polling" && <Polling />}
        {state.kind === "not_yet" && <Polling attempt={state.attempts} />}
        {state.kind === "ready" && <PassReady data={state.data} sessionId={search.session_id ?? null} />}
        {state.kind === "error" && <ErrorCard message={state.message} sessionId={search.session_id ?? null} />}
        {state.kind === "unauth" && <UnauthCard />}
      </div>
    </AppShell>
  );
}

function Polling({ attempt }: { attempt?: number }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-2xl">Confirming your pass…</CardTitle>
        <CardDescription>
          Your payment went through. We're waiting for the confirmation from Stripe to arrive
          — usually a second or two.
          {attempt ? <span className="ml-2 text-xs text-muted-foreground">(check {attempt}/10)</span> : null}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="h-1 w-full overflow-hidden rounded bg-muted">
          <div className="h-full w-1/3 animate-pulse rounded bg-palm" />
        </div>
      </CardContent>
    </Card>
  );
}

function PassReady({ data, sessionId }: { data: PassResp; sessionId: string | null }) {
  const pass = data.passes.find((p) => p.entitlement !== null) ?? data.passes[0];

  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-3xl text-palm">Your pass is ready 🎉</CardTitle>
        <CardDescription>
          Welcome to Taste of Tulum. Your account is your pass — bring your phone.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="rounded-xl border bg-sand p-6">
          <p className="mb-1 text-xs uppercase tracking-wider text-muted-foreground">Pass</p>
          <p className="mb-4 font-serif text-2xl text-palm">{pass.product.name}</p>
          <div className="grid grid-cols-2 gap-3 text-sm">
            <div>
              <p className="text-muted-foreground">Paid</p>
              <p><PriceTag cents={pass.purchase.total_cents} currency={pass.purchase.currency} /></p>
            </div>
            <div>
              <p className="text-muted-foreground">Ticket ID</p>
              <p className="font-mono text-xs">{pass.ticket_id.slice(0, 8)}…</p>
            </div>
          </div>
        </div>

        {pass.qr?.image_url && (
          <div className="flex flex-col items-center gap-2">
            <img
              src={pass.qr.image_url}
              alt="Pass QR"
              width={220}
              height={220}
              className="rounded-lg border bg-white p-3"
              loading="lazy"
            />
            <p className="text-xs text-muted-foreground">Show at any partner venue</p>
          </div>
        )}

        <div className="flex gap-3">
          <Button asChild className="flex-1">
            <Link to="/app/wallet">My tickets</Link>
          </Button>
          <Button asChild variant="outline" className="flex-1">
            <Link to="/app">Browse the festival</Link>
          </Button>
        </div>

        {sessionId && (
          <p className="text-center text-xs text-muted-foreground">
            Receipt: <span className="font-mono">{sessionId.slice(0, 24)}…</span>
          </p>
        )}

        <p className="text-center text-xs text-muted-foreground">All prices in US dollars.</p>
      </CardContent>
    </Card>
  );
}

function ErrorCard({ message, sessionId }: { message: string; sessionId: string | null }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-2xl text-palm">One second…</CardTitle>
        <CardDescription>{message}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <Button asChild>
          <Link to="/app/wallet">Check My tickets</Link>
        </Button>
        {sessionId && (
          <p className="text-xs text-muted-foreground">
            Reference: <span className="font-mono">{sessionId}</span>
          </p>
        )}
      </CardContent>
    </Card>
  );
}

function UnauthCard() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="font-serif text-2xl text-palm">Sign in to see your pass</CardTitle>
        <CardDescription>
          Your payment is safe with Stripe. Sign in with the same email you used to check out
          and your pass will appear.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Button asChild>
          <Link to="/login">Sign in</Link>
        </Button>
      </CardContent>
    </Card>
  );
}
